import TableStaticCell from 'components/Table/TableStatic/TableStaticCell';
import {
    TableStaticCheckboxCell,
    TableStaticSelectAllCell,
} from 'components/Table/TableStatic/TableStaticCheckbox';
import {FC, useRef} from 'react';
import {
    useCheckbox,
    useTableCell,
    useTableColumnHeader,
    useTableSelectAllCheckbox,
    useTableSelectionCheckbox,
    VisuallyHidden,
} from 'react-aria';
import {Key, Node, TableState, useToggleState} from 'react-stately';
import {twMerge} from 'tailwind-merge';

type TableCheckboxCellProps = {
    cell: Node<object>;
    state: TableState<unknown>;
};

export const TableCheckboxCell: FC<TableCheckboxCellProps> = ({
    cell,
    state,
}) => {
    // Table Cell Ref Hooks
    const tableCellRef = useRef(null);
    const {gridCellProps} = useTableCell({node: cell}, state, tableCellRef);

    // Checkbox Ref Hooks
    const {checkboxProps} = useTableSelectionCheckbox(
        {key: cell.parentKey as Key},
        state,
    );
    const checkboxRef = useRef(null);
    const toggleState = useToggleState(checkboxProps);
    const {inputProps} = useCheckbox(checkboxProps, toggleState, checkboxRef);

    return (
        <TableStaticCheckboxCell
            ref={tableCellRef}
            {...gridCellProps}
            inputProps={inputProps}
        />
    );
};

type TableSelectAllCellProps = {
    column: Node<object>;
    state: TableState<unknown>;
};

export const TableSelectAllCell: FC<TableSelectAllCellProps> = ({
    column,
    state,
}) => {
    // Table Header Cell ref hooks
    const tableHeaderRef = useRef(null);
    const {
        columnHeaderProps: {className, ...columnHeaderProps},
    } = useTableColumnHeader({node: column}, state, tableHeaderRef);

    // Checkbox Ref Hooks
    const {checkboxProps} = useTableSelectAllCheckbox(state);
    const checkboxRef = useRef(null);
    const checkBoxState = useToggleState(checkboxProps);
    const {inputProps} = useCheckbox(checkboxProps, checkBoxState, checkboxRef);

    if (state.selectionManager.selectionMode === 'single') {
        return (
            <TableStaticCell
                {...columnHeaderProps}
                className={twMerge('w-8', className)}
                ref={tableHeaderRef}
            >
                <VisuallyHidden>{checkboxProps['aria-label']}</VisuallyHidden>
            </TableStaticCell>
        );
    }

    const disabled = state.disabledKeys.size === state.collection.size;

    return (
        <TableStaticSelectAllCell
            {...columnHeaderProps}
            checkboxRef={checkboxRef}
            inputProps={disabled ? {...inputProps, disabled: true} : inputProps}
            ref={tableHeaderRef}
        />
    );
};
